import { Css, SelectField, useSnackbar } from "@homebound/beam";
import { useCallback } from "react";
import { StatusIndicator } from "src/components/StatusIndicator";
import { ProjectStatus } from "src/generated/graphqlTypes";
import { useSetProjectStatusMutation } from "src/routes/admin/components/ProjectStatusCell.generated";
import { ProjectAdminFragment } from "src/routes/admin/projects/Admin.generated";
import { HasIdAndName, safeEntries } from "src/utils";
import { projectStatusDetails } from "src/utils/mappers";

type ProjectStatusCellProps = { project: ProjectAdminFragment; isReadonly: boolean };

export function ProjectStatusCell({ project, isReadonly }: ProjectStatusCellProps) {
  const {
    status: { code: statusCode },
  } = project;
  const [projectStatusMutation] = useSetProjectStatusMutation();
  const { triggerNotice } = useSnackbar();

  const onSave = useCallback(
    async (projectStatus: ProjectStatus) => {
      await projectStatusMutation({ variables: { projectId: project.id, projectStatus } });
      triggerNotice({
        icon: "success",
        message: `${project.name} (${project.id}) status set to ${projectStatusDetails[projectStatus]}`,
      });
    },
    // TODO: validate this eslint-disable. It was automatically ignored as part of https://app.shortcut.com/homebound-team/story/40033/enable-react-hooks-exhaustive-deps-for-internal-frontend
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [project.id],
  );

  // Next create Id + Name pairs:
  const statusOptions: HasIdAndName[] = safeEntries(projectStatusDetails).map(([id, name]) => ({ id, name }));

  // If readonly, just show the status indicator and name
  return isReadonly ? (
    <div css={Css.df.aic.gap1.$}>
      <StatusIndicator status={statusCode} />
      {projectStatusDetails[statusCode]}
    </div>
  ) : (
    <SelectField
      value={statusCode as string}
      label="Project Status"
      options={statusOptions}
      fieldDecoration={({ id }) => <StatusIndicator status={id as ProjectStatus} />}
      getOptionMenuLabel={({ id, name }) => {
        return (
          <div css={Css.df.aic.gap1.$}>
            <StatusIndicator status={id as ProjectStatus} /> {name}
          </div>
        );
      }}
      onSelect={(v) => v && onSave(v as ProjectStatus)}
    />
  );
}
